import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, MessageSquare, HelpCircle, Settings, Trash2, Plus, BarChart3, Eye, MousePointerClick, Globe, FileText, ChevronUp, ClipboardList, Edit2, Save, BookOpen, Car, Upload, Check, X as XIcon, MapPin, Search, AlertTriangle } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function getAuthHeaders() {
  const token = sessionStorage.getItem("tripsi_token");
  return { headers: { Authorization: `Bearer ${token}` } };
}

function TabButton({ active, onClick, icon, label, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors ${active ? "bg-[#E60000] text-white" : "bg-[#141414] text-[#A3A3A3] hover:text-white border border-white/5"}`}>
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

// ============ ANALYTICS TAB ============
const PIE_COLORS = ["#E60000", "#FF6B6B", "#A3A3A3", "#666666"];

function StatCard({ icon, label, value, sub }) {
  return (
    <div className="bg-[#0A0A0A] border border-white/5 p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className="text-[#E60000]">{icon}</div>
        <span className="text-[#A3A3A3] text-xs uppercase tracking-wider">{label}</span>
      </div>
      <p className="font-['Outfit'] text-3xl font-bold text-white">{value}</p>
      {sub && <p className="text-[#A3A3A3] text-xs mt-1">{sub}</p>}
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1a1a1a] border border-white/10 px-3 py-2 text-xs">
      <p className="text-white font-semibold mb-1">{label}</p>
      {payload.map((p) => (<p key={p.name} style={{ color: p.color }} className="text-xs">{p.name}: {p.value}</p>))}
    </div>
  );
}

function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/analytics/summary`, getAuthHeaders())
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-[#A3A3A3] text-sm py-8 text-center">Loading analytics...</div>;
  if (!data) return <div className="text-[#A3A3A3] text-sm py-8 text-center">Failed to load analytics data.</div>;

  const { overview, top_pages, top_countries, top_referrers, daily_views, driver_stats, contact_stats } = data;

  const appPieData = [
    { name: "Rider App", value: overview.rider_clicks || 0 },
    { name: "Driver App", value: overview.driver_clicks || 0 },
  ].filter(d => d.value > 0);

  return (
    <div data-testid="analytics-tab" className="space-y-8">
      <h3 className="font-['Outfit'] text-lg font-bold text-white">Website Analytics</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={<Eye size={18} />} label="Total Views" value={overview.total_views || 0} sub={`${overview.today_views || 0} today`} />
        <StatCard icon={<Globe size={18} />} label="Unique Visitors" value={overview.unique_visitors || 0} />
        <StatCard icon={<MousePointerClick size={18} />} label="Download Clicks" value={overview.total_clicks || 0} sub={`${overview.week_views || 0} views this week`} />
        <StatCard icon={<BarChart3 size={18} />} label="Rider / Driver" value={`${overview.rider_clicks || 0} / ${overview.driver_clicks || 0}`} sub="Rider / Driver clicks" />
      </div>

      {(driver_stats || contact_stats) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {driver_stats && <StatCard icon={<Car size={18} />} label="Driver Apps" value={driver_stats.total || 0} sub={`${driver_stats.pending || 0} pending`} />}
          {driver_stats && <StatCard icon={<Check size={18} />} label="Approved Drivers" value={driver_stats.approved || 0} />}
          {contact_stats && <StatCard icon={<MessageSquare size={18} />} label="Total Contacts" value={contact_stats.total || 0} sub={`${contact_stats.week || 0} this week`} />}
          {top_referrers && <StatCard icon={<Globe size={18} />} label="Top Referrers" value={top_referrers.length || 0} sub="unique sources" />}
        </div>
      )}

      <div className="bg-[#0A0A0A] border border-white/5 p-6">
        <h4 className="font-['Outfit'] text-base font-semibold text-white mb-4">Page Views (Last 14 Days)</h4>
        {daily_views && daily_views.length > 0 ? (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={daily_views}>
              <XAxis dataKey="date" tick={{ fill: "#A3A3A3", fontSize: 11 }} tickFormatter={d => d.slice(5)} />
              <YAxis tick={{ fill: "#A3A3A3", fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="views" fill="#E60000" radius={[2, 2, 0, 0]} name="Views" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-[#A3A3A3] text-sm text-center py-8">No view data yet. Views will appear as visitors browse the site.</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-[#0A0A0A] border border-white/5 p-6">
          <h4 className="font-['Outfit'] text-base font-semibold text-white mb-4 flex items-center gap-2"><Eye size={16} className="text-[#E60000]" /> Top Pages</h4>
          {top_pages && top_pages.length > 0 ? (
            <div className="space-y-2">
              {top_pages.map(p => (
                <div key={p.page} className="flex items-center justify-between">
                  <span className="text-[#A3A3A3] text-sm truncate mr-2">{p.page}</span>
                  <span className="text-white text-sm font-semibold shrink-0">{p.views}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-[#A3A3A3] text-sm">No data yet</p>}
        </div>
        <div className="bg-[#0A0A0A] border border-white/5 p-6">
          <h4 className="font-['Outfit'] text-base font-semibold text-white mb-4 flex items-center gap-2"><Globe size={16} className="text-[#E60000]" /> Top Countries</h4>
          {top_countries && top_countries.length > 0 ? (
            <div className="space-y-2">
              {top_countries.map(c => (
                <div key={c.country} className="flex items-center justify-between">
                  <span className="text-[#A3A3A3] text-sm">{c.country}</span>
                  <span className="text-white text-sm font-semibold">{c.views}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-[#A3A3A3] text-sm">No data yet</p>}
        </div>
      </div>

      {appPieData.length > 0 && (
        <div className="bg-[#0A0A0A] border border-white/5 p-6">
          <h4 className="font-['Outfit'] text-base font-semibold text-white mb-4">Clicks by App Type</h4>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={appPieData} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                {appPieData.map((d, i) => <Cell key={d.name} fill={PIE_COLORS[i]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      {top_referrers && top_referrers.length > 0 && (
        <div className="bg-[#0A0A0A] border border-white/5 p-6">
          <h4 className="font-['Outfit'] text-base font-semibold text-white mb-4 flex items-center gap-2"><Globe size={16} className="text-[#E60000]" /> Top Referrers</h4>
          <div className="space-y-2">
            {top_referrers.map(r => (
              <div key={r.referrer} className="flex items-center justify-between">
                <span className="text-[#A3A3A3] text-sm truncate mr-2">{r.referrer}</span>
                <span className="text-white text-sm font-semibold shrink-0">{r.views}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============ FAQS TAB ============
function FAQsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ question: "", answer: "", order: 0 });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ question: "", answer: "", order: 0 });

  const load = useCallback(() => {
    axios.get(`${API}/cms/faqs/list`).then(r => setItems(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.question || !form.answer) return;
    await axios.post(`${API}/admin/faqs`, { ...form, order: items.length + 1 }, getAuthHeaders());
    setForm({ question: "", answer: "", order: 0 });
    load();
  };

  const update = async (fid) => {
    await axios.put(`${API}/admin/faqs/${fid}`, editForm, getAuthHeaders());
    setEditingId(null);
    load();
  };

  const remove = async (id) => {
    await axios.delete(`${API}/admin/faqs/${id}`, getAuthHeaders());
    load();
  };

  return (
    <div className="space-y-6">
      <h3 className="font-['Outfit'] text-lg font-bold text-white">Manage FAQs</h3>
      <div className="space-y-3">
        {items.map(f => (
          <div key={f.id} className="bg-[#0A0A0A] border border-white/5 p-4">
            {editingId === f.id ? (
              <div className="space-y-3">
                <Input value={editForm.question} onChange={e => setEditForm({...editForm, question: e.target.value})} placeholder="Question" className="bg-[#141414] border-white/10 text-white text-sm h-10" />
                <Textarea value={editForm.answer} onChange={e => setEditForm({...editForm, answer: e.target.value})} placeholder="Answer" rows={2} className="bg-[#141414] border-white/10 text-white text-sm" />
                <div className="flex gap-2">
                  <Button onClick={() => update(f.id)} className="bg-[#E60000] hover:bg-[#FF3B30] text-white text-sm"><Save size={14} className="mr-1" />Save</Button>
                  <Button onClick={() => setEditingId(null)} variant="outline" className="border-white/10 text-[#A3A3A3] text-sm">Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-white text-sm font-semibold">{f.question}</p>
                  <p className="text-[#A3A3A3] text-xs mt-1 line-clamp-2">{f.answer}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => { setEditingId(f.id); setEditForm({ question: f.question, answer: f.answer, order: f.order || 0 }); }} className="text-[#A3A3A3] hover:text-white"><Edit2 size={14} /></button>
                  <button onClick={() => remove(f.id)} className="text-[#A3A3A3] hover:text-[#E60000]"><Trash2 size={14} /></button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="bg-[#0A0A0A] border border-white/5 p-4 space-y-3">
        <p className="text-white text-sm font-semibold flex items-center gap-2"><Plus size={14} /> Add FAQ</p>
        <Input data-testid="admin-faq-question" value={form.question} onChange={e => setForm({...form, question: e.target.value})} placeholder="Question" className="bg-[#141414] border-white/10 text-white text-sm h-10" />
        <Textarea data-testid="admin-faq-answer" value={form.answer} onChange={e => setForm({...form, answer: e.target.value})} placeholder="Answer" rows={2} className="bg-[#141414] border-white/10 text-white text-sm" />
        <Button data-testid="admin-add-faq" onClick={add} className="bg-[#E60000] hover:bg-[#FF3B30] text-white text-sm">Add FAQ</Button>
      </div>
    </div>
  );
}

// ============ PAGES (CMS) TAB ============
function PagesTab() {
  const [activePage, setActivePage] = useState("privacy");
  const [content, setContent] = useState({});
  const [seo, setSeo] = useState({ title: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const pages = [
    { key: "privacy", label: "Privacy Policy" },
    { key: "terms", label: "Terms & Conditions" },
    { key: "home", label: "Home Page" },
    { key: "riders", label: "Riders" },
    { key: "drivers", label: "Drivers" },
    { key: "businesses", label: "Businesses" },
  ];

  useEffect(() => {
    setLoaded(false);
    axios.get(`${API}/cms/${activePage}`).then(r => {
      const data = r.data;
      setContent(data.content || {});
      setSeo(data.seo || { title: "", description: "" });
      setLoaded(true);
    }).catch(() => {
      setContent({});
      setSeo({ title: "", description: "" });
      setLoaded(true);
    });
  }, [activePage]);

  const sections = content.sections || [];

  const updateSection = (idx, field, value) => {
    const updated = [...sections];
    updated[idx] = { ...updated[idx], [field]: value };
    setContent({ ...content, sections: updated });
  };

  const addSection = () => {
    setContent({ ...content, sections: [...sections, { heading: `Section ${sections.length + 1}`, body: "" }] });
  };

  const removeSection = (idx) => {
    if (sections.length <= 1) return;
    setContent({ ...content, sections: sections.filter((_, i) => i !== idx) });
  };

  const moveSection = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= sections.length) return;
    const updated = [...sections];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    setContent({ ...content, sections: updated });
  };

  const save = async (isDraft = false) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/cms/${activePage}`, { content, seo, is_draft: isDraft }, getAuthHeaders());
    } catch (err) { console.error("CMS save failed:", err); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <h3 className="font-['Outfit'] text-lg font-bold text-white">Edit Pages (CMS)</h3>
      <p className="text-[#A3A3A3] text-sm">Manage content sections and SEO for each page.</p>
      <div className="flex flex-wrap gap-2">
        {pages.map(p => (
          <button key={p.key} onClick={() => setActivePage(p.key)} data-testid={`admin-page-${p.key}`} className={`px-4 py-2 text-sm font-medium transition-colors ${activePage === p.key ? "bg-[#E60000] text-white" : "bg-[#0A0A0A] border border-white/5 text-[#A3A3A3] hover:text-white"}`}>
            {p.label}
          </button>
        ))}
      </div>
      {loaded ? (
        <>
          <div className="bg-[#0A0A0A] border border-white/5 p-4 space-y-3">
            <p className="text-[#E60000] text-xs font-bold uppercase tracking-wider">SEO Settings</p>
            <Input value={seo.title || ""} onChange={e => setSeo({...seo, title: e.target.value})} placeholder="Page title (SEO)" className="bg-[#141414] border-white/10 text-white text-sm h-10" />
            <Input value={seo.description || ""} onChange={e => setSeo({...seo, description: e.target.value})} placeholder="Meta description (SEO)" className="bg-[#141414] border-white/10 text-white text-sm h-10" />
          </div>

          <div className="space-y-4">
            {sections.map((sec, idx) => (
              <div key={`section-${idx}`} className="bg-[#0A0A0A] border border-white/5 p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[#E60000] text-xs font-bold uppercase tracking-wider">Section {idx + 1}</span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => moveSection(idx, -1)} disabled={idx === 0} className="text-[#A3A3A3] hover:text-white disabled:opacity-20 p-1"><ChevronUp size={14} /></button>
                    <button onClick={() => moveSection(idx, 1)} disabled={idx === sections.length - 1} className="text-[#A3A3A3] hover:text-white disabled:opacity-20 p-1 rotate-180"><ChevronUp size={14} /></button>
                    <button onClick={() => removeSection(idx)} disabled={sections.length <= 1} className="text-[#A3A3A3] hover:text-[#E60000] disabled:opacity-20 p-1 ml-2"><Trash2 size={14} /></button>
                  </div>
                </div>
                <Input value={sec.heading} onChange={e => updateSection(idx, "heading", e.target.value)} placeholder="Section heading" className="bg-[#141414] border-white/10 text-white text-sm h-10 mb-3 font-semibold" />
                <Textarea value={sec.body} onChange={e => updateSection(idx, "body", e.target.value)} placeholder="Section content..." rows={4} className="bg-[#141414] border-white/10 text-white placeholder:text-white/30 text-sm" />
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={addSection} data-testid="admin-add-section" className="flex items-center gap-2 text-[#A3A3A3] hover:text-[#E60000] text-sm transition-colors"><Plus size={14} /> Add Section</button>
          </div>
          <div className="flex gap-3">
            <Button data-testid="admin-save-page" onClick={() => save(false)} disabled={saving} className="bg-[#E60000] hover:bg-[#FF3B30] text-white">{saving ? "Saving..." : "Publish Page"}</Button>
            <Button data-testid="admin-save-draft" onClick={() => save(true)} disabled={saving} variant="outline" className="border-white/10 text-[#A3A3A3] hover:text-white">{saving ? "Saving..." : "Save Draft"}</Button>
          </div>
        </>
      ) : (
        <p className="text-[#A3A3A3] text-sm">Loading...</p>
      )}
    </div>
  );
}

// ============ CONTACTS TAB ============
function ContactsTab() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios.get(`${API}/admin/contacts`, getAuthHeaders()).then(r => setItems(r.data || [])).catch(() => {});
  }, []);

  return (
    <div className="space-y-6">
      <h3 className="font-['Outfit'] text-lg font-bold text-white">Contact Submissions</h3>
      {items.length === 0 ? (
        <p className="text-[#A3A3A3] text-sm">No contact submissions yet.</p>
      ) : (
        <div className="space-y-3">
          {items.map(c => (
            <div key={c.id} className="bg-[#0A0A0A] border border-white/5 p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-white text-sm font-semibold">{c.name} &lt;{c.email}&gt;</p>
                <div className="flex items-center gap-3">
                  {c.inquiry_type && <span className="text-[#E60000] text-xs font-semibold uppercase">{c.inquiry_type}</span>}
                  <p className="text-[#A3A3A3] text-xs">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}</p>
                </div>
              </div>
              {c.phone && <p className="text-[#A3A3A3] text-xs mb-1">Phone: {c.phone}</p>}
              <p className="text-[#A3A3A3] text-sm">{c.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ AUDIT LOG TAB ============
function AuditLogTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/audit-log`, getAuthHeaders())
      .then(r => setLogs(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-[#A3A3A3] text-sm py-8 text-center">Loading audit log...</div>;

  return (
    <div className="space-y-6">
      <h3 className="font-['Outfit'] text-lg font-bold text-white">Audit Log</h3>
      {logs.length === 0 ? (
        <p className="text-[#A3A3A3] text-sm">No audit log entries yet.</p>
      ) : (
        <div className="space-y-2">
          {logs.map((log, i) => (
            <div key={log.timestamp || `log-${i}`} className="bg-[#0A0A0A] border border-white/5 px-4 py-3 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-[#E60000] text-xs font-bold uppercase shrink-0">{log.action}</span>
                <span className="text-[#A3A3A3] text-xs truncate">{log.details}</span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-[#A3A3A3] text-xs">{log.user}</span>
                <span className="text-[#A3A3A3]/60 text-xs">{log.timestamp ? new Date(log.timestamp).toLocaleString() : ""}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ BLOG TAB ============
function BlogTab() {
  const [posts, setPosts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: "", title_fr: "", slug: "", excerpt: "", excerpt_fr: "", content: "", content_fr: "", author: "", tags: [], cover_image: "", published: false });
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/blog/posts`, getAuthHeaders()).then(r => setPosts(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm({ title: "", title_fr: "", slug: "", excerpt: "", excerpt_fr: "", content: "", content_fr: "", author: "", tags: [], cover_image: "", published: false });
    setTagInput("");
    setShowForm(false);
    setEditingId(null);
  };

  const generateSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  const addTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      setForm({ ...form, tags: [...form.tags, tagInput.trim()] });
      setTagInput("");
    }
  };

  const removeTag = (tag) => setForm({ ...form, tags: form.tags.filter(t => t !== tag) });

  const save = async () => {
    if (!form.title || !form.slug || !form.content) return;
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/blog/posts/${editingId}`, form, getAuthHeaders());
      } else {
        await axios.post(`${API}/admin/blog/posts`, form, getAuthHeaders());
      }
      resetForm();
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save post");
    } finally { setSaving(false); }
  };

  const startEdit = (post) => {
    setForm({
      title: post.title, title_fr: post.title_fr || "", slug: post.slug,
      excerpt: post.excerpt, excerpt_fr: post.excerpt_fr || "",
      content: post.content, content_fr: post.content_fr || "",
      author: post.author, tags: post.tags || [], cover_image: post.cover_image || "", published: post.published
    });
    setEditingId(post.id);
    setShowForm(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await axios.delete(`${API}/admin/blog/posts/${id}`, getAuthHeaders());
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-['Outfit'] text-lg font-bold text-white">Blog Posts</h3>
        {!showForm && <Button data-testid="admin-new-blog-post" onClick={() => setShowForm(true)} className="bg-[#E60000] hover:bg-[#FF3B30] text-white text-sm"><Plus size={14} className="mr-1" />New Post</Button>}
      </div>

      {showForm && (
        <div className="bg-[#0A0A0A] border border-white/5 p-6 space-y-4">
          <p className="text-[#E60000] text-xs font-bold uppercase tracking-wider">{editingId ? "Edit Post" : "New Blog Post"}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[#A3A3A3] text-xs mb-1 block">Title (English) *</label>
              <Input data-testid="blog-title-en" value={form.title} onChange={e => { setForm({...form, title: e.target.value, slug: editingId ? form.slug : generateSlug(e.target.value)}); }} placeholder="Post title" className="bg-[#141414] border-white/10 text-white text-sm h-10" />
            </div>
            <div>
              <label className="text-[#A3A3A3] text-xs mb-1 block">Title (French)</label>
              <Input data-testid="blog-title-fr" value={form.title_fr} onChange={e => setForm({...form, title_fr: e.target.value})} placeholder="Titre de l'article" className="bg-[#141414] border-white/10 text-white text-sm h-10" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[#A3A3A3] text-xs mb-1 block">URL Slug *</label>
              <Input data-testid="blog-slug" value={form.slug} onChange={e => setForm({...form, slug: e.target.value})} placeholder="post-url-slug" className="bg-[#141414] border-white/10 text-white text-sm h-10 font-mono" />
            </div>
            <div>
              <label className="text-[#A3A3A3] text-xs mb-1 block">Author *</label>
              <Input data-testid="blog-author" value={form.author} onChange={e => setForm({...form, author: e.target.value})} placeholder="Author name" className="bg-[#141414] border-white/10 text-white text-sm h-10" />
            </div>
          </div>
          <div>
            <label className="text-[#A3A3A3] text-xs mb-1 block">Cover Image</label>
            <div className="flex gap-2">
              <Input value={form.cover_image} onChange={e => setForm({...form, cover_image: e.target.value})} placeholder="Paste URL or upload..." className="bg-[#141414] border-white/10 text-white text-sm h-10 flex-1" />
              <label className="flex items-center gap-2 bg-[#E60000]/20 hover:bg-[#E60000]/30 text-[#E60000] px-4 h-10 text-sm font-semibold cursor-pointer transition-colors shrink-0">
                <Upload size={14} />Upload
                <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const fd = new FormData();
                  fd.append("file", file);
                  try {
                    const { data } = await axios.post(`${API}/admin/upload`, fd, { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" } });
                    setForm(prev => ({...prev, cover_image: `${process.env.REACT_APP_BACKEND_URL}${data.url}`}));
                  } catch (err) { alert("Upload failed: " + (err.response?.data?.detail || err.message)); }
                  e.target.value = "";
                }} />
              </label>
            </div>
            {form.cover_image && <img src={form.cover_image} alt="Cover preview" className="mt-2 h-24 object-cover border border-white/5" />}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[#A3A3A3] text-xs mb-1 block">Excerpt (English)</label>
              <Textarea value={form.excerpt} onChange={e => setForm({...form, excerpt: e.target.value})} placeholder="Brief summary..." rows={2} className="bg-[#141414] border-white/10 text-white text-sm" />
            </div>
            <div>
              <label className="text-[#A3A3A3] text-xs mb-1 block">Excerpt (French)</label>
              <Textarea value={form.excerpt_fr} onChange={e => setForm({...form, excerpt_fr: e.target.value})} placeholder="Résumé bref..." rows={2} className="bg-[#141414] border-white/10 text-white text-sm" />
            </div>
          </div>
          <div>
            <label className="text-[#A3A3A3] text-xs mb-1 block">Content (English) *</label>
            <Textarea data-testid="blog-content-en" value={form.content} onChange={e => setForm({...form, content: e.target.value})} placeholder="Full post content... Use ## for headings, blank lines for paragraphs" rows={8} className="bg-[#141414] border-white/10 text-white text-sm font-mono" />
          </div>
          <div>
            <label className="text-[#A3A3A3] text-xs mb-1 block">Content (French)</label>
            <Textarea value={form.content_fr} onChange={e => setForm({...form, content_fr: e.target.value})} placeholder="Contenu complet... Utilisez ## pour les titres" rows={8} className="bg-[#141414] border-white/10 text-white text-sm font-mono" />
          </div>
          <div>
            <label className="text-[#A3A3A3] text-xs mb-1 block">Tags</label>
            <div className="flex gap-2 mb-2 flex-wrap">
              {form.tags.map(tag => (
                <span key={tag} className="bg-[#E60000]/10 text-[#E60000] text-xs font-semibold px-2 py-1 flex items-center gap-1">{tag}<button onClick={() => removeTag(tag)} className="hover:text-white">&times;</button></span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addTag())} placeholder="Add tag..." className="bg-[#141414] border-white/10 text-white text-sm h-10 flex-1" />
              <Button onClick={addTag} type="button" variant="outline" className="border-white/10 text-[#A3A3A3] text-sm">Add</Button>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-[#A3A3A3]">
              <input type="checkbox" checked={form.published} onChange={e => setForm({...form, published: e.target.checked})} className="accent-[#E60000]" />
              Published
            </label>
          </div>
          <div className="flex gap-3">
            <Button data-testid="admin-save-blog-post" onClick={save} disabled={saving} className="bg-[#E60000] hover:bg-[#FF3B30] text-white">{saving ? "Saving..." : editingId ? "Update Post" : "Create Post"}</Button>
            <Button onClick={resetForm} variant="outline" className="border-white/10 text-[#A3A3A3]">Cancel</Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {posts.map(p => (
          <div key={p.id} className="bg-[#0A0A0A] border border-white/5 p-4 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-xs font-bold px-2 py-0.5 ${p.published ? "bg-[#E60000]/20 text-[#E60000]" : "bg-white/5 text-[#A3A3A3]"}`}>{p.published ? "PUBLISHED" : "DRAFT"}</span>
                <span className="text-[#A3A3A3] text-xs">{p.published_at ? new Date(p.published_at).toLocaleDateString() : ""}</span>
              </div>
              <p className="text-white text-sm font-semibold truncate">{p.title}</p>
              <p className="text-[#A3A3A3] text-xs mt-0.5">By {p.author} &bull; /{p.slug}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => startEdit(p)} className="text-[#A3A3A3] hover:text-white"><Edit2 size={14} /></button>
              <button onClick={() => remove(p.id)} className="text-[#A3A3A3] hover:text-[#E60000]"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {posts.length === 0 && !showForm && <p className="text-[#A3A3A3] text-sm">No blog posts yet. Click "New Post" to create one.</p>}
      </div>
    </div>
  );
}

// ============ DRIVER APPLICATIONS TAB ============
function DriverApplicationsTab() {
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    Promise.all([
      axios.get(`${API}/admin/drivers/applications`, getAuthHeaders()),
      axios.get(`${API}/admin/drivers/applications/stats`, getAuthHeaders()),
    ]).then(([appsRes, statsRes]) => {
      setApps(appsRes.data || []);
      setStats(statsRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    await axios.put(`${API}/admin/drivers/applications/${id}/status`, { status }, getAuthHeaders());
    load();
  };

  if (loading) return <div className="text-[#A3A3A3] text-sm py-8 text-center">Loading applications...</div>;

  const statusColors = { pending: "bg-yellow-500/20 text-yellow-400", approved: "bg-green-500/20 text-green-400", rejected: "bg-red-500/20 text-red-400" };

  return (
    <div className="space-y-6">
      <h3 className="font-['Outfit'] text-lg font-bold text-white">Driver Applications</h3>
      {stats && (
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-[#0A0A0A] border border-white/5 p-4 text-center"><p className="text-2xl font-bold text-white">{stats.total}</p><p className="text-[#A3A3A3] text-xs">Total</p></div>
          <div className="bg-[#0A0A0A] border border-white/5 p-4 text-center"><p className="text-2xl font-bold text-yellow-400">{stats.pending}</p><p className="text-[#A3A3A3] text-xs">Pending</p></div>
          <div className="bg-[#0A0A0A] border border-white/5 p-4 text-center"><p className="text-2xl font-bold text-green-400">{stats.approved}</p><p className="text-[#A3A3A3] text-xs">Approved</p></div>
          <div className="bg-[#0A0A0A] border border-white/5 p-4 text-center"><p className="text-2xl font-bold text-red-400">{stats.rejected}</p><p className="text-[#A3A3A3] text-xs">Rejected</p></div>
        </div>
      )}
      {apps.length === 0 ? (
        <p className="text-[#A3A3A3] text-sm">No driver applications yet.</p>
      ) : (
        <div className="space-y-3">
          {apps.map(app => (
            <div key={app.id} className="bg-[#0A0A0A] border border-white/5 p-4">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <p className="text-white text-sm font-semibold">{app.full_name}</p>
                  <p className="text-[#A3A3A3] text-xs">{app.email} &bull; {app.phone}</p>
                  <p className="text-[#A3A3A3] text-xs mt-1">{app.city}, {app.province}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-xs font-bold px-2 py-1 uppercase ${statusColors[app.status] || ""}`}>{app.status}</span>
                  <span className="text-[#A3A3A3] text-xs">{app.created_at ? new Date(app.created_at).toLocaleDateString() : ""}</span>
                </div>
              </div>
              <div className="text-[#A3A3A3] text-xs space-y-1">
                {(app.vehicle_make || app.vehicle_model) && <p>Vehicle: {app.vehicle_year} {app.vehicle_make} {app.vehicle_model}</p>}
                {app.license_class && <p>License: {app.license_class}</p>}
                {app.experience_years > 0 && <p>Experience: {app.experience_years} years</p>}
                {app.message && <p className="mt-2 italic text-[#A3A3A3]/80">"{app.message}"</p>}
              </div>
              {app.status === "pending" && (
                <div className="flex gap-2 mt-3">
                  <Button onClick={() => updateStatus(app.id, "approved")} className="bg-green-600 hover:bg-green-700 text-white text-xs h-8 px-3"><Check size={12} className="mr-1" />Approve</Button>
                  <Button onClick={() => updateStatus(app.id, "rejected")} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs h-8 px-3"><XIcon size={12} className="mr-1" />Reject</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ SEO HEALTH TAB ============
function SEOTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/seo/health`, getAuthHeaders())
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-[#A3A3A3] text-sm py-8 text-center">Loading SEO data...</div>;
  if (!data) return <div className="text-[#A3A3A3] text-sm py-8 text-center">Failed to load.</div>;

  const scoreColor = data.score >= 80 ? "text-green-400" : data.score >= 50 ? "text-yellow-400" : "text-red-400";

  return (
    <div className="space-y-6">
      <h3 className="font-['Outfit'] text-lg font-bold text-white">SEO Health Dashboard</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-[#0A0A0A] border border-white/5 p-5 text-center">
          <p className={`text-4xl font-bold ${scoreColor}`}>{data.score}</p>
          <p className="text-[#A3A3A3] text-xs mt-1">SEO Score</p>
        </div>
        <div className="bg-[#0A0A0A] border border-white/5 p-5 text-center">
          <p className="text-2xl font-bold text-white">{data.cities_count}</p>
          <p className="text-[#A3A3A3] text-xs mt-1">City Pages</p>
        </div>
        <div className="bg-[#0A0A0A] border border-white/5 p-5 text-center">
          <p className="text-2xl font-bold text-white">{data.published_blogs}</p>
          <p className="text-[#A3A3A3] text-xs mt-1">Blog Posts</p>
        </div>
        <div className="bg-[#0A0A0A] border border-white/5 p-5 text-center">
          <p className="text-2xl font-bold text-white">{data.faqs_count}</p>
          <p className="text-[#A3A3A3] text-xs mt-1">FAQs</p>
        </div>
      </div>

      <div className="bg-[#0A0A0A] border border-white/5 p-5">
        <h4 className="font-['Outfit'] text-base font-semibold text-white mb-3 flex items-center gap-2"><AlertTriangle size={16} className="text-yellow-400" /> Recommendations</h4>
        {data.recommendations?.length > 0 ? (
          <ul className="space-y-2">
            {data.recommendations.map((r, i) => <li key={`rec-${i}`} className="text-[#A3A3A3] text-sm flex items-start gap-2"><span className="text-yellow-400 shrink-0">•</span>{r}</li>)}
          </ul>
        ) : <p className="text-green-400 text-sm">All SEO checks passed!</p>}
      </div>

      <div className="bg-[#0A0A0A] border border-white/5 p-5">
        <h4 className="font-['Outfit'] text-base font-semibold text-white mb-3">SEO Assets</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-[#A3A3A3]">Sitemap</span><a href="/api/sitemap.xml" target="_blank" rel="noopener noreferrer" className="text-[#E60000] hover:text-[#FF3B30]">/api/sitemap.xml →</a></div>
          <div className="flex justify-between"><span className="text-[#A3A3A3]">robots.txt</span><a href="/robots.txt" target="_blank" rel="noopener noreferrer" className="text-[#E60000] hover:text-[#FF3B30]">/robots.txt →</a></div>
          <div className="flex justify-between"><span className="text-[#A3A3A3]">RSS Feed</span><a href="/api/rss/blog" target="_blank" rel="noopener noreferrer" className="text-[#E60000] hover:text-[#FF3B30]">/api/rss/blog →</a></div>
          <div className="flex justify-between"><span className="text-[#A3A3A3]">GA4 Status</span><span className="text-yellow-400 text-xs">Placeholder — add your G-XXXXXXXXXX ID</span></div>
        </div>
      </div>
    </div>
  );
}

// ============ CITIES TAB ============
function CitiesTab() {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    axios.get(`${API}/admin/cities`, getAuthHeaders())
      .then(r => setCities(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteCity = async (slug) => {
    if (!window.confirm(`Delete ${slug}?`)) return;
    await axios.delete(`${API}/admin/cities/${slug}`, getAuthHeaders());
    load();
  };

  if (loading) return <div className="text-[#A3A3A3] text-sm py-8 text-center">Loading cities...</div>;

  const statusBadge = (s) => {
    if (s === "active") return <span className="text-xs font-bold px-2 py-0.5 bg-green-500/20 text-green-400">ACTIVE</span>;
    return <span className="text-xs font-bold px-2 py-0.5 bg-yellow-500/20 text-yellow-400">COMING SOON</span>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-['Outfit'] text-lg font-bold text-white">City Landing Pages</h3>
        <p className="text-[#A3A3A3] text-xs">{cities.length} cities</p>
      </div>
      <p className="text-[#A3A3A3] text-sm">Each city automatically gets an SEO-optimized landing page at <code className="text-[#E60000]">/cities/[slug]</code> with unique schema, sitemap entry, and local content.</p>
      <div className="space-y-3">
        {cities.map(c => (
          <div key={c.slug} className="bg-[#0A0A0A] border border-white/5 p-4 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <MapPin size={14} className="text-[#E60000]" />
                <span className="text-white text-sm font-semibold">{c.name}</span>
                {statusBadge(c.status)}
                <span className="text-[#A3A3A3] text-xs">{c.province_code}</span>
              </div>
              <p className="text-[#A3A3A3] text-xs truncate">/cities/{c.slug} &bull; {c.booking_method === "app" ? "App booking" : "Dispatch"}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <a href={`/cities/${c.slug}`} target="_blank" rel="noopener noreferrer" className="text-[#A3A3A3] hover:text-white text-xs">View →</a>
              <button onClick={() => deleteCity(c.slug)} className="text-[#A3A3A3] hover:text-[#E60000]"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ MAIN DASHBOARD ============
export default function AdminDashboard() {
  const [tab, setTab] = useState("analytics");
  const navigate = useNavigate();

  useEffect(() => {
    const token = sessionStorage.getItem("tripsi_token");
    if (!token) { navigate("/admin"); return; }
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => navigate("/admin"));
  }, [navigate]);

  const handleLogout = () => {
    sessionStorage.removeItem("tripsi_token");
    navigate("/admin");
  };

  const tabs = [
    { key: "analytics", icon: <BarChart3 size={16} />, label: "Analytics" },
    { key: "pages", icon: <FileText size={16} />, label: "Pages" },
    { key: "blog", icon: <BookOpen size={16} />, label: "Blog" },
    { key: "drivers", icon: <Car size={16} />, label: "Drivers" },
    { key: "cities", icon: <MapPin size={16} />, label: "Cities" },
    { key: "seo", icon: <Search size={16} />, label: "SEO" },
    { key: "faqs", icon: <HelpCircle size={16} />, label: "FAQs" },
    { key: "contacts", icon: <MessageSquare size={16} />, label: "Contacts" },
    { key: "audit", icon: <ClipboardList size={16} />, label: "Audit Log" },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen py-24 md:py-32">
      <div className="max-w-5xl mx-auto px-6 md:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-['Outfit'] text-2xl md:text-3xl font-bold text-white">CMS Dashboard</h1>
              <p className="text-[#A3A3A3] text-sm mt-1">Manage your Tripsi website content</p>
            </div>
            <Button onClick={handleLogout} data-testid="admin-logout" variant="outline" className="border-white/10 text-[#A3A3A3] hover:text-white hover:border-white/20">
              <LogOut size={16} className="mr-2" />Logout
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 mb-8">
            {tabs.map(t => (
              <TabButton key={t.key} testId={`admin-tab-${t.key}`} active={tab === t.key} onClick={() => setTab(t.key)} icon={t.icon} label={t.label} />
            ))}
          </div>

          <div className="bg-[#141414] border border-white/5 p-6 md:p-8">
            {tab === "analytics" && <AnalyticsTab />}
            {tab === "pages" && <PagesTab />}
            {tab === "blog" && <BlogTab />}
            {tab === "drivers" && <DriverApplicationsTab />}
            {tab === "cities" && <CitiesTab />}
            {tab === "seo" && <SEOTab />}
            {tab === "faqs" && <FAQsTab />}
            {tab === "contacts" && <ContactsTab />}
            {tab === "audit" && <AuditLogTab />}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
